import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { collection, query, orderBy, onSnapshot, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { adManagementService } from '@/lib/ad-management-service';
import { AdSpot } from '@/types/ad-management';

interface UseAdManagementReturn {
  adSpots: AdSpot[];
  loading: boolean;
  error: string | null;
  getAdsByLocation: (location: string) => AdSpot[];
  getActiveAdsByLocation: (location: string) => AdSpot[];
  trackImpression: (adSpotId: string) => Promise<void>;
  trackClick: (adSpotId: string) => Promise<void>;
  refreshAds: () => Promise<void>;
}

export function useAdManagement(): UseAdManagementReturn {
  const [adSpots, setAdSpots] = useState<AdSpot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Set up real-time listener for better performance
    if (unsubscribeRef.current) return;
    
    setLoading(true);
    setError(null);

    try {
      if (!db) return;
      const unsubscribe = onSnapshot(
        query(
          collection(db, 'ad_spots'),
          orderBy('priority', 'desc'),
          orderBy('createdAt', 'desc')
        ),
        (querySnapshot) => {
          try {
            const spots: AdSpot[] = querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            } as AdSpot));
            
            setAdSpots(spots);
            setError(null);
            setLoading(false);
          } catch (err) {
            setError('Failed to process ad spots data');
            setLoading(false);
          }
        },
        (err) => {
          setError(`Failed to fetch ad spots: ${err.message}`);
          setLoading(false);
        }
      );
      
      unsubscribeRef.current = unsubscribe;

      return () => {
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
      };
    } catch (err) {
      setError('Failed to initialize ad spots listener');
      setLoading(false);
    }
  }, []);

  const loadAdSpots = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const spots = await adManagementService.getAdSpots();
      setAdSpots(spots);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ad spots');
    } finally {
      setLoading(false);
    }
  }, []);

  // Helper to check if ad matches a location (supports both single and multi-location)
  const adMatchesLocation = (ad: AdSpot, location: string): boolean => {
    // Check locations array first (multi-location support)
    if (ad.locations && ad.locations.length > 0) {
      return ad.locations.includes(location);
    }
    // Fall back to single location field
    return ad.location === location;
  };

  const getAdsByLocation = useMemo(() => {
    return (location: string): AdSpot[] => {
      return adSpots.filter(ad => adMatchesLocation(ad, location));
    };
  }, [adSpots]);

  const getActiveAdsByLocation = useMemo(() => {
    return (location: string): AdSpot[] => {
      const now = new Date().toISOString();
      return adSpots.filter(ad => {
        // Check location match (supports multi-location)
        if (!adMatchesLocation(ad, location)) return false;
        
        // Check if enabled
        if (!ad.enabled) return false;
        
        // Check start date
        if (ad.startDate && ad.startDate > now) return false;
        
        // Check end date
        if (ad.endDate && ad.endDate < now) return false;
        
        return true;
      }).sort((a, b) => b.priority - a.priority); // Sort by priority descending
    };
  }, [adSpots]);

  const trackImpression = async (adSpotId: string) => {
    try {
      await adManagementService.trackImpression(adSpotId);
    } catch (err) {
      // Silently fail impression tracking in production
    }
  };

  const trackClick = async (adSpotId: string) => {
    try {
      await adManagementService.trackClick(adSpotId);
    } catch (err) {
      // Silently fail click tracking in production
    }
  };

  const refreshAds = useCallback(async () => {
    await loadAdSpots();
  }, [loadAdSpots]);

  return {
    adSpots,
    loading,
    error,
    getAdsByLocation,
    getActiveAdsByLocation,
    trackImpression,
    trackClick,
    refreshAds
  };
}
